import React, { useCallback, useContext, useMemo } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { graphql, useFragment } from 'react-relay'
import { useField } from 'formik'
import { XStack, YStack } from 'tamagui'

import { Text } from '../Text'
import { GuildPrimaryPhoto } from '../Guild/GuildPrimaryPhoto'
import { UserPrimaryPhoto } from '../User/UserPrimaryPhoto'
import { FormFieldContext } from '../Form/shared'
import { SearchableCombobox, ComboboxSection } from '../Form/SearchableCombobox'

import { getRecentEventOwnerIds } from '../../data/recentEventOwners'

import {
  OwnerSectionKey,
  groupOwnerOptions,
  normalizeSearchText as normalize,
} from './eventOwnerOptions'

import {
  EventOwnerPicker_user$key,
  EventOwnerPicker_user$data,
} from './__generated__/EventOwnerPicker_user.graphql'
import { EventOwnerPickerGuildOption_guild$key } from './__generated__/EventOwnerPickerGuildOption_guild.graphql'

type GuildNode = NonNullable<
  EventOwnerPicker_user$data['guildsForNewEvents']['nodes'][number]
>

type OwnerOption =
  | { kind: 'guild'; rowId: string; searchText: string; guild: GuildNode }
  | { kind: 'user'; rowId: string; searchText: string }

export type EventOwnerChange = {
  rowId: string
  kind: 'guild' | 'user'
  defaultTimeZone: string | null
}

/**
 * Searchable picker for who owns a new (or moved) Event: one of the Guilds
 * the person can create Events for, or the person themselves. Writes the
 * selected owner row ID to the surrounding FormField.
 */
export const EventOwnerPicker = ({
  user,
  onOwnerChange,
  excludeOwnerRowId,
}: {
  user: EventOwnerPicker_user$key
  onOwnerChange?: (owner: EventOwnerChange) => void
  /** Hide an owner, e.g. the Event's current owner when moving it. */
  excludeOwnerRowId?: string
}) => {
  const { t } = useLingui()

  const data = useFragment(
    graphql`
      fragment EventOwnerPicker_user on User {
        rowId
        firstName
        lastName

        ...UserPrimaryPhotoNew_user

        guildsForNewEvents(first: 500) {
          nodes {
            rowId
            name
            defaultTimeZone

            networks: rankedNetworks(membershipStatus: ACCEPTED, first: 20) {
              nodes {
                name
              }
            }

            ...EventOwnerPickerGuildOption_guild
          }
        }
      }
    `,
    user
  )

  const formFieldContext = useContext(FormFieldContext)
  const [field, , helpers] = useField<string>(formFieldContext.name)

  const userName =
    [data.firstName, data.lastName].filter(Boolean).join(' ') || t`Your account`

  const allOptions = useMemo<OwnerOption[]>(() => {
    const guildOptions: OwnerOption[] = data.guildsForNewEvents.nodes
      .filter(
        (guild): guild is GuildNode =>
          !!guild && guild.rowId !== excludeOwnerRowId
      )
      .map((guild) => ({
        kind: 'guild',
        rowId: guild.rowId,
        guild,
        searchText: normalize(
          [guild.name, ...guild.networks.nodes.map((n) => n?.name ?? '')].join(
            ' '
          )
        ),
      }))

    const userOption: OwnerOption[] =
      data.rowId && data.rowId !== excludeOwnerRowId
        ? [
            {
              kind: 'user',
              rowId: data.rowId,
              searchText: normalize(`${userName} ${t`Personal Event`}`),
            },
          ]
        : []

    return [...guildOptions, ...userOption]
  }, [data, excludeOwnerRowId, userName, t])

  const getSections = useCallback(
    (query: string): ComboboxSection<OwnerOption>[] => {
      const titles: Record<OwnerSectionKey, string> = {
        results: t`Results`,
        recent: t`Recently used`,
        guilds: t`Your Guilds`,
        personal: t`Just you`,
      }

      return groupOwnerOptions(allOptions, query, getRecentEventOwnerIds()).map(
        (section) => ({
          key: section.key,
          title: titles[section.key],
          items: section.options,
        })
      )
    },
    [allOptions, t]
  )

  const handleSelect = useCallback(
    (option: OwnerOption) => {
      void helpers.setValue(option.rowId)
      void helpers.setTouched(true, false)
      onOwnerChange?.({
        rowId: option.rowId,
        kind: option.kind,
        defaultTimeZone:
          option.kind === 'guild'
            ? (option.guild.defaultTimeZone ?? null)
            : null,
      })
    },
    [helpers, onOwnerChange]
  )

  const selectedOption = allOptions.find(
    (option) => option.rowId === field.value
  )

  const renderOwner = (option: OwnerOption) =>
    option.kind === 'guild' ? (
      <GuildOptionContent guild={option.guild} />
    ) : (
      <UserOptionContent user={data} name={userName} />
    )

  return (
    <SearchableCombobox<OwnerOption>
      getSections={getSections}
      getKey={(option) => `${option.kind}-${option.rowId}`}
      onSelect={handleSelect}
      isSelected={(option) => option.rowId === field.value}
      placeholder={t`Search your Guilds`}
      label={t`Choose who hosts this Event`}
      valueLabel={
        selectedOption?.kind === 'guild'
          ? selectedOption.guild.name
          : selectedOption
            ? userName
            : undefined
      }
      onOpen={formFieldContext.onFocus}
      onClose={formFieldContext.onBlur}
      triggerMinHeight={48}
      renderOption={renderOwner}
      renderEmpty={(query) => (
        <Text size='$4' color='$colorMuted'>
          <Trans>
            No Guilds match &ldquo;{query}&rdquo;. You can create Events for
            Guilds you organize or edit.
          </Trans>
        </Text>
      )}
      renderTrigger={() =>
        selectedOption ? (
          renderOwner(selectedOption)
        ) : (
          <Text size='$5' color='$colorMuted' flex={1}>
            <Trans>Choose a Guild</Trans>
          </Text>
        )
      }
    />
  )
}

const GuildOptionContent = ({
  guild,
}: {
  guild: EventOwnerPickerGuildOption_guild$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventOwnerPickerGuildOption_guild on Guild {
        name
        type

        myMembership {
          membershipLevel
        }

        networks: rankedNetworks(membershipStatus: ACCEPTED, first: 20) {
          nodes {
            name

            myMembership {
              membershipLevel
            }
          }
        }

        ...GuildPrimaryPhotoNew_guild
      }
    `,
    guild
  )

  const isDirectManager =
    data.myMembership?.membershipLevel === 'ORGANIZER' ||
    data.myMembership?.membershipLevel === 'EDITOR'
  // Name the Network that gives the viewer access, when that's why the Guild
  // is listed; otherwise the Guild's largest Network.
  const organizedNetwork = data.networks.nodes.find(
    (network) => network?.myMembership?.membershipLevel === 'ORGANIZER'
  )
  const networkName = (
    (!isDirectManager && organizedNetwork) ||
    data.networks.nodes[0]
  )?.name

  let subtitle: React.ReactNode = null
  if (data.type === 'NETWORK') {
    subtitle = <Trans>Network</Trans>
  } else if (networkName && !isDirectManager) {
    subtitle = (
      <Trans>
        Part of {networkName}. You&apos;ll co-host as a Network Organizer
      </Trans>
    )
  } else if (networkName) {
    subtitle = <Trans>Part of {networkName}</Trans>
  }

  return (
    <XStack flex={1} gap='$3' alignItems='center'>
      <GuildPrimaryPhoto guild={data} size='$8' />

      <YStack flex={1} gap={2}>
        <Text size='$5' fontWeight='$6' color='$colorStrong' numberOfLines={1}>
          {data.name}
        </Text>

        {/* Two lines, so the co-host note isn't cut off on phones. */}
        {subtitle ? (
          <Text size='$4' color='$colorMuted' numberOfLines={2}>
            {subtitle}
          </Text>
        ) : null}
      </YStack>
    </XStack>
  )
}

const UserOptionContent = ({
  user,
  name,
}: {
  user: EventOwnerPicker_user$data
  name: string
}) => (
  <XStack flex={1} gap='$3' alignItems='center'>
    <UserPrimaryPhoto user={user} size='$8' />

    <YStack flex={1} gap={2}>
      <Text size='$5' fontWeight='$6' color='$colorStrong' numberOfLines={1}>
        {name}
      </Text>

      <Text size='$4' color='$colorMuted' numberOfLines={2}>
        <Trans>Personal Event, not part of a Guild</Trans>
      </Text>
    </YStack>
  </XStack>
)
